<template>
  <div class="flow-report">
    <div class="report-header">
      <div>
        <h3>{{ title }}</h3>
        <p>{{ $t('CAMPAIGN_FLOW.REPORT.TITLE') }}</p>
      </div>
      <woot-button variant="smooth" @click="$emit('close')">
        {{ $t('CAMPAIGN_FLOW.REPORT.BACK') }}
      </woot-button>
    </div>

    <form class="filters" @submit.prevent="applyFilters">
      <label>
        {{ $t('CAMPAIGN_FLOW.REPORT.STAGE') }}
        <select v-model="draft.status_id">
          <option value="">{{ $t('CAMPAIGN_FLOW.REPORT.ALL_STAGES') }}</option>
          <option v-for="node in nodes" :key="node.card_id" :value="node.card_id">
            {{ node.label || node.card_id }}
          </option>
        </select>
      </label>
      <label>
        {{ $t('CAMPAIGN_FLOW.REPORT.STATUS') }}
        <select v-model="draft.status">
          <option value="">{{ $t('CAMPAIGN_FLOW.REPORT.ALL_STATUSES') }}</option>
          <option value="pending">{{ $t('CAMPAIGN_FLOW.REPORT.PENDING') }}</option>
          <option value="executed">{{ $t('CAMPAIGN_FLOW.REPORT.EXECUTED') }}</option>
        </select>
      </label>
      <label>
        {{ $t('CAMPAIGN_FLOW.REPORT.DATE_FROM') }}
        <input v-model="draft.started_from" type="date" />
      </label>
      <label>
        {{ $t('CAMPAIGN_FLOW.REPORT.DATE_TO') }}
        <input v-model="draft.started_to" type="date" />
      </label>
      <label>
        {{ $t('CAMPAIGN_FLOW.REPORT.SEARCH') }}
        <input v-model="draft.q" type="search" />
      </label>
      <woot-button size="small" type="submit">
        {{ $t('CAMPAIGN_FLOW.REPORT.APPLY') }}
      </woot-button>
      <woot-button size="small" variant="smooth" type="button" @click="download('csv')">
        {{ $t('CAMPAIGN_FLOW.REPORT.EXPORT_CSV') }}
      </woot-button>
      <woot-button size="small" variant="smooth" type="button" @click="download('xlsx')">
        {{ $t('CAMPAIGN_FLOW.REPORT.EXPORT_XLSX') }}
      </woot-button>
    </form>
    <p class="hint">{{ $t('CAMPAIGN_FLOW.REPORT.DATE_HINT') }}</p>
    <p v-if="applied.status_id" class="hint">
      {{ $t('CAMPAIGN_FLOW.REPORT.PASSED_IGNORES_STAGE') }}
    </p>
    <p v-if="exportError" class="error">{{ $t('CAMPAIGN_FLOW.REPORT.EXPORT_ERROR') }}</p>

    <p v-if="summaryState === 'error'" class="error">
      {{ $t('CAMPAIGN_FLOW.REPORT.REPORT_ERROR') }}
    </p>

    <div v-if="summaryState === 'ready'" class="cards">
      <report-stats-card
        :heading="$t('CAMPAIGN_FLOW.REPORT.TOTAL')"
        :point="totals.executions"
        :desc="$t('CAMPAIGN_FLOW.REPORT.TOTAL_DESC')"
      />
      <report-stats-card
        :heading="$t('CAMPAIGN_FLOW.REPORT.IN_PROGRESS')"
        :point="totals.in_progress"
        :desc="$t('CAMPAIGN_FLOW.REPORT.IN_PROGRESS_DESC')"
      />
      <report-stats-card
        :heading="$t('CAMPAIGN_FLOW.REPORT.COMPLETED')"
        :point="totals.completed"
        :desc="$t('CAMPAIGN_FLOW.REPORT.COMPLETED_DESC')"
      />
      <report-stats-card
        :heading="$t('CAMPAIGN_FLOW.REPORT.RESPONSE')"
        :point="responseLabel"
        :info-text="$t('CAMPAIGN_FLOW.REPORT.RESPONSE_DESC')"
        :desc="$t('CAMPAIGN_FLOW.REPORT.RESPONSE_DESC')"
      />
      <report-stats-card
        :heading="$t('CAMPAIGN_FLOW.REPORT.NOT_STARTED')"
        :point="summary.not_started"
        :info-text="$t('CAMPAIGN_FLOW.REPORT.NOT_STARTED_DESC')"
        :desc="$t('CAMPAIGN_FLOW.REPORT.NOT_STARTED_DESC')"
      />
    </div>
    <p v-else-if="summaryState === 'loading'">{{ $t('CAMPAIGN_FLOW.REPORT.LOADING') }}</p>

    <section v-if="summaryState === 'ready'">
      <h4>{{ $t('CAMPAIGN_FLOW.REPORT.FUNNEL') }}</h4>
      <p class="hint">{{ $t('CAMPAIGN_FLOW.REPORT.FUNNEL_NOTE') }}</p>
      <div class="funnel">
        <article v-for="node in nodes" :key="node.card_id" class="funnel-row">
          <strong>{{ node.label || node.card_id }}</strong>
          <button type="button" @click="openDrawer(node, 'current')">
            {{ $t('CAMPAIGN_FLOW.REPORT.NOW') }}: {{ node.current }}
            <template v-if="percent(node.current_percent)">
              ({{ percent(node.current_percent) }})
            </template>
          </button>
          <button type="button" @click="openDrawer(node, 'passed')">
            {{ $t('CAMPAIGN_FLOW.REPORT.PASSED') }}: {{ node.passed }}
            <template v-if="percent(node.passed_percent)">
              ({{ percent(node.passed_percent) }})
            </template>
            <template v-else>({{ $t('CAMPAIGN_FLOW.REPORT.UNAVAILABLE') }})</template>
          </button>
          <p v-for="edge in edgesFrom(node.card_id)" :key="`${edge.source}-${edge.target}`">
            <span v-if="edge.conversion === null || edge.conversion === undefined">
              {{ edgeLabel(edge) }} — {{ $t('CAMPAIGN_FLOW.REPORT.CONVERSION_UNAVAILABLE') }}
            </span>
            <span v-else>
              {{ edgeLabel(edge) }} — {{ percent(edge.conversion) }}
              ({{ edge.transitions }}/{{ edge.origin_passed }}
              {{ $t('CAMPAIGN_FLOW.REPORT.CONVERSION_BASE') }})
            </span>
          </p>
        </article>
      </div>
    </section>

    <p v-if="listState === 'error'" class="error">{{ $t('CAMPAIGN_FLOW.REPORT.ERROR') }}</p>
    <p v-else-if="listState === 'loading'">{{ $t('CAMPAIGN_FLOW.REPORT.LOADING') }}</p>
    <p v-else-if="!contacts.length">{{ $t('CAMPAIGN_FLOW.REPORT.EMPTY') }}</p>
    <table v-else class="woot-table">
      <thead>
        <tr>
          <th>{{ $t('CAMPAIGN_FLOW.REPORT.NAME') }}</th>
          <th>{{ $t('CAMPAIGN_FLOW.REPORT.PHONE') }}</th>
          <th>{{ $t('CAMPAIGN_FLOW.REPORT.CURRENT_STAGE') }}</th>
          <th>{{ $t('CAMPAIGN_FLOW.REPORT.CAMPAIGN_RESPONSE') }}</th>
          <th>{{ $t('CAMPAIGN_FLOW.REPORT.STATUS') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="contact in contacts" :key="contact.id">
          <td>{{ contact.name || '—' }}</td>
          <td>{{ contact.phone_number || '—' }}</td>
          <td>{{ contact.stage_label || contact.status_id }}</td>
          <td>
            {{ formatDate(contact.last_campaign_response_at) || $t('CAMPAIGN_FLOW.REPORT.NO_RESPONSE') }}
          </td>
          <td>{{ statusLabel(contact.status) }}</td>
        </tr>
      </tbody>
    </table>
    <p v-if="listState === 'ready' && meta.total_pages">
      {{ $t('CAMPAIGN_FLOW.REPORT.PAGE', { page: meta.page, pages: meta.total_pages }) }}
      — {{ meta.total }}
    </p>
    <table-footer
      v-if="listState === 'ready'"
      :current-page="meta.page"
      :page-size="50"
      :total-count="meta.total"
      @page-change="changePage"
    />

    <campaign-flow-stage-drawer
      v-if="drawer"
      :show="true"
      :campaign-id="campaignId"
      :card-id="drawer.cardId"
      :card-label="drawer.label"
      :population="drawer.population"
      :base-filters="drawer.filters"
      :forced-empty="drawer.forcedEmpty"
      @close="drawer = null"
    />
  </div>
</template>

<script>
import CampaignsFlowAPI from 'dashboard/api/campaignsFlow';
import ReportStatsCard from 'dashboard/components/widgets/ReportStatsCard.vue';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import CampaignFlowStageDrawer from './CampaignFlowStageDrawer.vue';

const emptyFilters = () => ({
  status_id: '',
  status: '',
  started_from: '',
  started_to: '',
  q: '',
});

export default {
  components: { ReportStatsCard, TableFooter, CampaignFlowStageDrawer },
  props: {
    campaignId: { type: Number, required: true },
    title: { type: String, default: '' },
  },
  data() {
    return {
      draft: emptyFilters(),
      applied: emptyFilters(),
      summary: null,
      contacts: [],
      meta: { page: 1, total: 0, total_pages: 0 },
      summaryState: 'loading',
      listState: 'loading',
      requestId: 0,
      exportError: false,
      drawer: null,
    };
  },
  computed: {
    totals() {
      return (this.summary && this.summary.totals) || {};
    },
    nodes() {
      return (this.summary && this.summary.nodes) || [];
    },
    responseLabel() {
      if (this.totals.response_rate === null || this.totals.response_rate === undefined) {
        return this.$t('CAMPAIGN_FLOW.REPORT.UNAVAILABLE');
      }
      return this.percent(this.totals.response_rate);
    },
  },
  watch: {
    campaignId() {
      this.drawer = null;
      this.load(1);
    },
  },
  mounted() {
    this.load(1);
  },
  beforeDestroy() {
    this.requestId += 1;
  },
  methods: {
    percent(value) {
      if (value === null || value === undefined) return '';
      return `${Math.round(value * 1000) / 10}%`;
    },
    queryParams(extra = {}) {
      const params = { ...extra };
      if (this.applied.status) params.status = this.applied.status;
      if (this.applied.started_from) params.started_from = this.applied.started_from;
      if (this.applied.started_to) params.started_to = this.applied.started_to;
      if (this.applied.q) params.q = this.applied.q;
      if (this.applied.status_id) params.status_id = this.applied.status_id;
      return params;
    },
    async load(page) {
      const requestId = ++this.requestId;
      this.summaryState = 'loading';
      this.listState = 'loading';
      this.exportError = false;
      const summaryParams = this.queryParams();
      const listParams = this.queryParams({ population: 'all', page, per_page: 50 });
      try {
        const [summaryResponse, listResponse] = await Promise.all([
          CampaignsFlowAPI.executionSummary(this.campaignId, summaryParams),
          CampaignsFlowAPI.executions(this.campaignId, listParams),
        ]);
        if (requestId !== this.requestId) return;
        const summary = summaryResponse.data;
        const list = listResponse.data;
        if (!summary || !Array.isArray(summary.nodes) || !summary.totals) {
          this.summaryState = 'error';
        } else {
          this.summary = summary;
          this.summaryState = 'ready';
        }
        if (!list || !Array.isArray(list.contacts) || !list.meta) {
          this.listState = 'error';
        } else {
          this.contacts = list.contacts;
          this.meta = list.meta;
          this.listState = 'ready';
        }
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.summaryState = 'error';
        this.listState = 'error';
      }
    },
    applyFilters() {
      this.applied = { ...this.draft };
      this.drawer = null;
      this.load(1);
    },
    changePage(page) {
      this.loadPage(page);
    },
    async loadPage(page) {
      const requestId = ++this.requestId;
      this.listState = 'loading';
      try {
        const { data } = await CampaignsFlowAPI.executions(
          this.campaignId,
          this.queryParams({ population: 'all', page, per_page: 50 })
        );
        if (requestId !== this.requestId) return;
        if (!data || !Array.isArray(data.contacts) || !data.meta) {
          this.listState = 'error';
          return;
        }
        this.contacts = data.contacts;
        this.meta = data.meta;
        this.listState = 'ready';
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.listState = 'error';
      }
    },
    statusLabel(status) {
      return status === 'executed'
        ? this.$t('CAMPAIGN_FLOW.REPORT.EXECUTED')
        : this.$t('CAMPAIGN_FLOW.REPORT.PENDING');
    },
    formatDate(value) {
      if (!value) return '';
      return String(value).replace('T', ' ').slice(0, 16);
    },
    edgesFrom(cardId) {
      return ((this.summary && this.summary.edges) || []).filter(edge => edge.source === cardId);
    },
    edgeLabel(edge) {
      const target = this.nodes.find(node => node.card_id === edge.target);
      return `${this.$t('CAMPAIGN_FLOW.REPORT.CONVERSION')} → ${target ? target.label || target.card_id : edge.target}`;
    },
    openDrawer(node, population) {
      const filters = {};
      if (this.applied.status) filters.status = this.applied.status;
      if (this.applied.started_from) filters.started_from = this.applied.started_from;
      if (this.applied.started_to) filters.started_to = this.applied.started_to;
      if (this.applied.q) filters.q = this.applied.q;
      const stageMismatch = population === 'current'
        && this.applied.status_id
        && this.applied.status_id !== node.card_id;
      this.drawer = {
        cardId: node.card_id,
        label: node.label || node.card_id,
        population,
        filters,
        forcedEmpty: stageMismatch,
      };
    },
    async download(format) {
      this.exportError = false;
      try {
        const response = await CampaignsFlowAPI.exportExecutions(
          this.campaignId,
          this.queryParams({ population: 'all', format })
        );
        const type = response.headers['content-type'] || '';
        if (type.includes('application/json')) {
          this.exportError = true;
          return;
        }
        const url = window.URL.createObjectURL(new Blob([response.data], { type }));
        const link = document.createElement('a');
        const match = /filename="?([^"]+)"?/.exec(response.headers['content-disposition'] || '');
        link.href = url;
        link.download = match ? match[1] : `relatorio.${format}`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.exportError = true;
      }
    },
  },
};
</script>

<style scoped>
.flow-report {
  padding: 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.report-header,
.filters,
.cards {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}
.report-header,
.filters {
  align-items: flex-end;
}
.cards {
  align-items: flex-start;
}
.cards .report-card {
  align-self: flex-start;
}
.filters label {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 1.2rem;
}
.hint {
  color: var(--s-600);
  margin: 0;
}
.error {
  color: var(--r-500);
}
.funnel {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(26rem, 1fr));
  gap: 1.2rem;
}
.funnel-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.8rem;
  margin: 0;
  padding: 1.4rem;
  border: 1px solid var(--s-100);
  border-radius: 1.2rem;
  background: var(--color-surface);
}
.funnel-row strong {
  font-size: 1.6rem;
  line-height: 1.3;
}
.funnel-row button,
.funnel-row p {
  margin: 0;
  font-size: 1.35rem;
  line-height: 1.4;
}
.funnel-row button {
  background: var(--s-25);
  border: 0;
  border-radius: 16px;
  padding: 0.6rem 1rem;
  cursor: pointer;
}
.woot-table {
  width: 100%;
}
</style>
